import { CircularProgress } from '@mui/material';
import { PlusCircle, TrashSimple, UserMinus } from '@phosphor-icons/react';
import { HandCoins } from '@phosphor-icons/react/dist/ssr';
import { useEffect, useState } from 'react';
import { BsArrowLeft } from 'react-icons/bs';
import { FiChevronDown } from 'react-icons/fi';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FinalBillPrint } from 'src/components/bill-print';
import { getDiscountStorageValue } from 'src/components/discount-storage';
import Order from 'src/components/order';
import { CurrencyAmount } from 'src/containers/currency';
import { fetchPaymentSuccess } from 'src/redux/action/sales.action';
import { RootState } from 'src/redux/reducers';
import Service from 'src/setup/network';
import { toastAlert } from 'src/utils/toastAlert';

interface PaymentMethod {
  id: string;
  name: string;
  logo: string;
  needsQr: boolean;
  image: string;
  totalAmountRecieved: string;
}
const Payment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orders, currentUser, orderInfo } = useSelector(
    ({ currentOrderData }: RootState) => currentOrderData
  );
  const [currentPaymentIndex, setCurrentPaymentIndex] = useState<any>(0);
  const [currentPayment, setCurrentPayment] = useState<any>(null);
  const [discountInfo, setDiscountInfo] = useState<any>(
    getDiscountStorageValue(orderInfo)
  );
  const [totalAmountRecieved, setTotalAmountRecieved] = useState<
    number | string
  >('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [payResp, setPayResp] = useState<any>(null);
  const [paymentMethodsArray, setPaymentMethodsArray] = useState<
    PaymentMethod[]
  >([
    {
      id: '',
      name: '',
      logo: '',
      needsQr: false,
      image: '',
      totalAmountRecieved: '',
    },
  ]);
  const all_payment_types =
    currentPayment?.id === 'credit'
      ? [{ name: 'Full credit' }]
      : currentPayment?.id === 'others'
      ? paymentMethodsArray
      : [{ name: currentPayment?.name }];

  if (!location.state) {
    navigate('/dine-in');
  }

  const tableId = location.state?.tableId || null;
  const wholeTable = location.state?.wholeTable;
  const dispatch = useDispatch();

  const { settingData } = useSelector(
    ({ settingData }: RootState) => settingData,
    shallowEqual
  );
  const businessName = settingData?.businessInfo?.name;
  const businessAddress = settingData?.businessInfo?.address;
  const panNumber = settingData?.businessInfo?.pan;

  const { payments } = useSelector(({ salesData }: RootState) => salesData);

  useEffect(() => {
    let sel = null;
    if (currentPaymentIndex === 'credit') {
      sel = {
        id: 'credit',
        needsQr: false,
        isEditable: true,
        isSelected: true,
      };
    } else if (currentPaymentIndex === payments.length) {
      sel = {
        id: 'others',
        needsQr: false,
        isEditable: true,
        isSelected: true,
      };
    } else sel = payments?.[currentPaymentIndex];
    setCurrentPayment(sel);
    setTotalAmountRecieved(0);
  }, [payments, currentPaymentIndex]);

  useEffect(() => {
    const fetchQr = async () => {
      const userInfo = JSON.parse(
        window.localStorage.getItem('userInfo') || ''
      );
      try {
        const res = await Service.get(
          `/settings/paymentMethod/selected/all/${userInfo.businessInfo.id}`
        );
        dispatch(fetchPaymentSuccess(res?.data?.qrs));
      } catch (err: any) {}
    };
    fetchQr();
  }, []);

  useEffect(() => {
    const totalPrice = paymentMethodsArray.reduce(
      (sum: number, item: PaymentMethod) =>
        sum +
        (item.totalAmountRecieved ? parseFloat(item.totalAmountRecieved) : 0),
      0
    );
    setTotalAmountRecieved(totalPrice);
  }, [paymentMethodsArray]);

  const total = orderInfo?.total;

  useEffect(() => {
    if (!payResp) return;
    navigate('/dine-in');
    handlePrintFinalBill();
  }, [payResp]);

  const getItemDiscount = (item: any) => {
    const foundItem = discountInfo?.discountsArr?.find(
      (x: any) => x.id === item.variantId
    );
    if (foundItem) {
      const { type, value } = foundItem;
      return { value, type };
    }
    return null;
  };

  const makePayment = async () => {
    if (!currentPayment || paymentLoading) return;

    if (currentPayment.id === 'others' && isErrorvalidatePaymentMethods())
      return;

    if (paymentMethodsArray.length === 0) {
      if (
        totalAmountRecieved === '' ||
        totalAmountRecieved === 0 ||
        (typeof totalAmountRecieved === 'string' &&
          /^\s*$/.test(totalAmountRecieved))
      ) {
        toastAlert('error', `Provide received amount`);
        return;
      }
    }

    const data = {
      orderId: wholeTable ? null : location.state?.orderId,
      tableId: wholeTable ? tableId : null,
      code: null,
      items: orders.items?.map((x) => ({
        ...x,
        discountInfo: getItemDiscount(x),
        modifiers: x.selectedModifiers,
      })),
      transactionsInfo:
        currentPayment.id === 'credit'
          ? []
          : currentPayment.id === 'others'
          ? paymentMethodsArray.map((x) => ({
              originId: x.id,
              amount: parseFloat(x.totalAmountRecieved),
            }))
          : [
              {
                originId: currentPayment.id,
                amount: totalAmountRecieved,
              },
            ],
      discountInfo: discountInfo?.discountsWhole,
      customerInfo: currentUser
        ? {
            id: currentUser.id,
          }
        : null,
    };

    setPaymentLoading(true);
    try {
      const resp = await Service.post('/payments/receipt', data);
      setPayResp(resp?.data?.receipt);
      toastAlert('success', `Paid successfully`);
      setPaymentLoading(false);
      // setShowModal(true);
    } catch (err: any) {
      toastAlert('error', err.response?.data?.message || err.message || 'Payment failed');
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    setTotalAmountRecieved('');
  }, [currentPayment]);

  const handlePrintFinalBill = (orders?: any) => {
    const prtContent = document.getElementById(`finalBillPrint`);
    if (!prtContent) return;
    const printWindow = window.open(
      '',
      '',
      'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0'
    );
    if (printWindow) {
      printWindow.document.write(prtContent.innerHTML);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    } else {
      console.error('Failed to open a new window for printing.');
    }
  };

  const getTotal = () => {
    return total;
  };

  const isErrorvalidatePaymentMethods = () => {
    let error = true;
    paymentMethodsArray.forEach((element) => {
      if (element.id === '') toastAlert('error', 'Invalid payment type');
      else if (isNaN(parseFloat(element.totalAmountRecieved)))
        toastAlert('error', 'Invalid amount');
      else error = false;
    });
    return error;
  };

  const handleAddPaymentMethod = () => {
    const existingMethods = [...paymentMethodsArray];
    existingMethods.push({
      id: '',
      name: '',
      logo: '',
      needsQr: false,
      image: '',
      totalAmountRecieved: '',
    });
    setPaymentMethodsArray(existingMethods);
  };

  const handleDeletePaymentMethod = (index: number) => {
    const existingMethods = [...paymentMethodsArray];
    existingMethods.splice(index, 1);
    setPaymentMethodsArray(existingMethods);
  };

  const OrderPaymentEach = (index: number) => (
    <div
      key={'mul_pay+method_' + index}
      className="border border-solid border-almost-white py-4 px-3 rounded-xl"
    >
      <div className="flex justify-end pb-2">
        <TrashSimple
          onClick={(e) => handleDeletePaymentMethod(index)}
          size={20}
          className="text-secondary-0"
        />
      </div>
      <div className="flex flex-col">
        <label
          className="text-sm mb-2 text-text-black block font-Outfit font-normal"
          htmlFor="totalAmountReceived"
        >
          Payment Type
        </label>
        <select
          className="w-full h-[50px] rounded-xl border outline-none cursor-pointer bg-pure-white
          border-border-color-dark appearance-none px-[20px] font-Outfit font-normal text-sm capitalize"
          value={
            paymentMethodsArray?.[index] ? paymentMethodsArray?.[index]?.id : ''
          }
          onChange={(event) => {
            const selectedPayment = payments.find(
              (x: any) => x.id === event.target.value
            );
            const existingMethods = [...paymentMethodsArray];
            existingMethods[index] = {...selectedPayment, totalAmountRecieved: ''};
            setPaymentMethodsArray(existingMethods);
          }}
        >
          <option value="" disabled>
            Select payment type
          </option>
          {payments.map((floor: PaymentMethod) => (
            <option
              key={floor.id}
              value={floor.id}
              className="capitalize font-Outfit"
            >
              <p className="font-Outfit">{floor.name}</p>
            </option>
          ))}
        </select>
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
          <FiChevronDown className="text-gray-400" />
        </div>
      </div>

      {paymentMethodsArray?.[index]?.needsQr &&
        paymentMethodsArray?.[index]?.image && (
          <div className="h-[200px] flex justify-center items-center ">
            <img src={paymentMethodsArray?.[index]?.image} alt="qr" />
          </div>
        )}
      <div className="h-fit rounded-xl mt-3">
        <label
          className="text-sm mb-2 text-text-black block font-Outfit font-normal"
          htmlFor="totalAmountReceived"
        >
          Amount Received
        </label>
        <input
          type="text"
          className={`w-full focus:outline-none appearance-none
            text-text-black placeholder:capitalize rounded-xl py-3 block placeholder:text-sm px-3
             bg-pure-white text-sm ring-1 ring-light-grey focus-within:ring-1
              focus-within:ring-primary focus-within:outline-0"   `}
          value={
            // currentAddedValue.length > 0
            //   ? totalPrice
            //   : totalAmountRecieved
            paymentMethodsArray[index]?.totalAmountRecieved
          }
          onChange={(e) => {
            const existingMethods = [...paymentMethodsArray];
            existingMethods[index].totalAmountRecieved = e.target.value;
            setPaymentMethodsArray(existingMethods);
          }}
        />
      </div>
    </div>
  );

  const OthersPayment = () => (
    <div className="w-[416px] h-fit p-6 flex flex-col gap-6 border border-[#F1F0F6] rounded-xl mb-4">
      <div className="w-[368px] h-[86px] flex flex-col justify-center px-3 rounded-xl border border-[#F1F0F6]">
        <div className="text-[#949CC0] font-Outfit font-semibold text-xs leading-[18px]">
          Total Amount Due
        </div>
        <div className="text-[#2B2B2B] font-Outfit font-semibold text-[26px] leading-[32px]">
          <CurrencyAmount amount={(getTotal())}/>
        </div>
      </div>

      {paymentMethodsArray?.map((each: any, i: number) => OrderPaymentEach(i))}
      <div
        onClick={handleAddPaymentMethod}
        className="border border-dashed border-border-color-dark flex cursor-pointer
      py-3 rounded-[5px] justify-center items-center text-secondary-0 gap-2"
      >
        <PlusCircle />
        <span>Add payment section</span>
      </div>
      {totalAmountRecieved ? (
        <div className="w-full flex justify-between mt-3">
          <div className="font-Outfit font-normal text-base text-[#7F8397]">
            {Number(totalAmountRecieved) > (getTotal())
              ? 'Change'
              : 'Credit'}
          </div>
          <div className="font-Outfit font-semibold text-[#2B2B2B]">
            <CurrencyAmount amount={(totalAmountRecieved as number) - (getTotal())}/>
          </div>
        </div>
      ) : null}

      <div
        className="w-[150px] ml-auto h-[41px] bg-primary rounded-lg flex justify-center items-center cursor-pointer font-Outfit font-semibold text-sm text-[#FFFFFF] "
        onClick={() => makePayment()}
      >
        {paymentLoading ? (
          <CircularProgress style={{ color: '#FFF' }} size={25} />
        ) : (
          'Make payment'
        )}
      </div>
    </div>
  );
  const CreditPayment = () => (
    <div className="w-[416px] h-fit p-6 flex flex-col gap-6 border border-[#F1F0F6] rounded-xl mb-4">
      <div className="w-[368px] h-[86px] flex flex-col justify-center px-3 rounded-xl border border-[#F1F0F6]">
        <div className="text-[#949CC0] font-Outfit font-semibold text-xs leading-[18px]">
          Total Amount Due
        </div>
        <div className="text-[#2B2B2B] font-Outfit font-semibold text-[26px] leading-[32px]">
          <CurrencyAmount amount={(getTotal())}/>
        </div>
      </div>
      <div className="w-[368px] h-[86px] flex flex-col justify-center px-3 rounded-xl border border-[#F1F0F6]">
        <div className="text-[#949CC0] font-Outfit font-semibold text-xs leading-[18px]">
          Total Paid
        </div>
        <div className="text-[#2B2B2B] font-Outfit font-semibold text-[26px] leading-[32px]">
          <CurrencyAmount amount={0}/>
        </div>
      </div>

      <div
        className="w-[150px] ml-auto h-[41px] bg-primary rounded-lg flex justify-center items-center cursor-pointer font-Outfit font-semibold text-sm text-[#FFFFFF] "
        onClick={() => makePayment()}
      >
        {paymentLoading ? (
          <CircularProgress style={{ color: '#FFF' }} size={25} />
        ) : (
          'Make credit payment'
        )}
      </div>
    </div>
  );
  return (
    <>
      <div className="w-full h-[calc(100vh-75px)] text-black flex flex-row justify-between gap-4">
        <div className="w-full h-full flex flex-col gap-[18px]">
          <div className="h-[41px] text-black absolute -top-[68px]">
            <div
              className="flex flex-row gap-3 items-center cursor-pointer h-full"
              onClick={() => {
                navigate(-1);
              }}
            >
              <BsArrowLeft fontSize={20} />
              <div className="font-Outfit font-semibold text-[20px] leading-[32px]">
                Payment
              </div>
            </div>
            <div className="flex flex-row gap-6 mt-2">
              <div className="w-[250px] xl:w-[299px] h-fit rounded-xl border border-[#F1F0F6] overflow-hidden">
                {payments.map((item: any, index: number) => {
                  return (
                    <div
                      key={item.id}
                      className={`h-[48px] w-full flex border-b border-[#F1F0F6] items-center px-8 gap-3 font-Sarabun font-normal text-base  cursor-pointer ${
                        currentPaymentIndex === index
                          ? 'bg-[#D9E0FF] text-[#4062FF] font-semibold'
                          : ''
                      } hover:bg-[#D9E0FF]`}
                      onClick={() => {
                        setCurrentPaymentIndex(index);
                      }}
                    >
                      <img src={item.logo} className="w-[24px] h-[24px]" />
                      <div className="">
                        {item.name.charAt(0).toUpperCase() + item.name.slice(1)}
                      </div>
                    </div>
                  );
                })}
                {payments?.length > 0 && (
                  <div>
                    <div
                      className={`h-[48px] w-full flex border-b border-[#F1F0F6] items-center px-8 gap-3 font-Sarabun font-normal text-base  cursor-pointer ${
                        currentPaymentIndex === 'credit' ? 'bg-[#D9E0FF] ' : ''
                      } hover:bg-[#D9E0FF]`}
                      onClick={() => {
                        setCurrentPaymentIndex('credit');
                      }}
                    >
                      <UserMinus
                        color={
                          currentPaymentIndex === 'credit' ? '#4062FF' : ''
                        }
                        className="w-[24px] h-[24px]"
                      />
                      <div
                        className={
                          currentPaymentIndex === 'credit'
                            ? 'text-[#4062FF] font-semibold'
                            : ''
                        }
                      >
                        Full credit
                      </div>
                    </div>
                    <div
                      className={`h-[48px] w-full flex border-b border-[#F1F0F6] items-center px-8 gap-3 font-Sarabun font-normal text-base  cursor-pointer ${
                        currentPaymentIndex === payments.length
                          ? 'bg-[#D9E0FF] '
                          : ''
                      } hover:bg-[#D9E0FF]`}
                      onClick={() => {
                        setCurrentPaymentIndex(payments.length);
                      }}
                    >
                      <HandCoins
                        color={
                          currentPaymentIndex === payments.length
                            ? '#4062FF'
                            : ''
                        }
                        className="w-[24px] h-[24px]"
                      />
                      <div
                        className={
                          currentPaymentIndex === payments.length
                            ? 'text-[#4062FF] font-semibold'
                            : ''
                        }
                      >
                        Split
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {currentPayment &&
                currentPayment.id !== 'others' &&
                currentPayment.id !== 'credit' && (
                  <div className="w-[416px] h-fit p-6 flex flex-col gap-6 border border-[#F1F0F6] rounded-xl mb-4">
                    <div className="w-[368px] h-[86px] flex flex-col justify-center px-3 rounded-xl border border-[#F1F0F6]">
                      <div className="text-[#949CC0] font-Outfit font-semibold text-xs leading-[18px]">
                        Total Amount Due
                      </div>
                      <div className="text-[#2B2B2B] font-Outfit font-semibold text-[26px] leading-[32px]">
                        <CurrencyAmount amount={(getTotal())}/>
                      </div>
                    </div>

                    {currentPayment?.needsQr && currentPayment?.image && (
                      <div className="w-[368px] h-[368px] flex justify-center items-center ">
                        <img src={currentPayment.image} alt="qr" />
                      </div>
                    )}

                    {
                      <div className=" w-[368px] h-fit border border-[#F1F0F6] rounded-xl py-[16px] px-[14px]">
                        <div className="flex justify-between items-center">
                          <label
                            className="text-sm mb-2 text-text-black block font-Outfit font-normal"
                            htmlFor="totalAmountReceived"
                          >
                            Amount Received
                          </label>
                          <button onClick={()=>setTotalAmountRecieved(getTotal().toString())}
                            className="text-sm mb-2 text-link block font-Outfit font-semibold">
                            Pay full
                          </button>
                        </div>
                        <input
                          type="text"
                          className={`w-full focus:outline-none appearance-none text-text-black placeholder:capitalize rounded-xl py-3 block placeholder:text-sm px-3 bg-pure-white text-sm ring-1 ring-light-grey focus-within:ring-1 focus-within:ring-primary focus-within:outline-0"   `}
                          value={
                            // currentAddedValue.length > 0
                            //   ? totalPrice
                            //   : totalAmountRecieved
                            totalAmountRecieved
                          }
                          onChange={(e) =>
                            setTotalAmountRecieved(e.target.value)
                          }
                        />

                        {totalAmountRecieved && (
                          <div className="w-full flex justify-between mt-3">
                            <div className="font-Outfit font-normal text-base text-[#7F8397]">
                              {Number(totalAmountRecieved) >
                              (getTotal())
                                ? 'Change'
                                : 'Credit'}
                            </div>
                            <div className="font-Outfit font-semibold text-[#2B2B2B]">
                              <CurrencyAmount amount=
                              {(totalAmountRecieved as number) -
                                (getTotal())}/>
                            </div>
                          </div>
                        )}
                      </div>
                    }
                    <div
                      className="w-[150px] ml-auto h-[41px] bg-primary rounded-lg flex justify-center items-center cursor-pointer font-Outfit font-semibold text-sm text-[#FFFFFF] "
                      onClick={() => makePayment()}
                    >
                      {paymentLoading ? (
                        <CircularProgress style={{ color: '#FFF' }} size={25} />
                      ) : (
                        'Make payment'
                      )}
                    </div>
                  </div>
                )}
              {currentPayment &&
                currentPayment.id == 'credit' &&
                CreditPayment()}
              {currentPayment &&
                currentPayment.id == 'others' &&
                OthersPayment()}
            </div>
          </div>
        </div>
        <Order
          paymentOption={payments[currentPaymentIndex]}
          payment={true}
          orderId={location.state?.orderId || null}
          discountInfo={discountInfo}
          setDiscountInfo={setDiscountInfo}
        />
        {FinalBillPrint(
          orders,
          businessName,
          businessAddress,
          panNumber,
          orderInfo,
          currentUser,
          all_payment_types,
          totalAmountRecieved,
          payResp
        )}
      </div>
    </>
  );
};

export default Payment;
